import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import {
  firebaseSendLoginEmail,
  firebaseConfirmLoginUser,
  checkUserExists,
  getUserDetails,
  firebaseLoginEmailAndPassword,
  logoutUser,
  updateLoggedInUserPassword,
  resetPassword,
  firebaseConfirmPasswordReset,
  firebaseVerifyPasswordResetCode,
  getPasswordResetErrorMessage,
  getLoginErrorMessage,
  isStalePasswordResetCodeError,
  isRetryablePasswordResetError,
  checkUserIsSetupWithCompany,
  firebaseSendPasswordResetEmail,
  newUserNotifyDill,
} from '../../redux/services/authService';
import { AppInputField } from '../../components/general/AppInputField/AppInputField';
import { AppPasswordRequirements } from '../../components/general/AppPasswordRequirements/AppPasswordRequirements';
import { usePasswordValidation } from '../../utils/hooks/usePasswordValidation';
import dillLogoWithText from '../../assets/svg/dill_logo_with_text.svg';
import { AppButton } from '../../components/general/AppButton/AppButton';
import { myFirebaseAuth } from '../../firebase/config';
import { getBasicSupplier } from '../../redux/services/buyersService';
import { resetUserCheck, updateSendLoginEmailState } from '../../redux/globalSlices/authSlice';
import { z } from 'zod';
import { AppCheckBox } from '../../components/general/AppCheckBox/AppCheckBox';
import { Checks, ShieldCheck } from 'phosphor-react';
import {
  buildSupplierPath,
  clearDeclinedReferPathForLoggedInUser,
  clearLoginSupplierId,
  getSavedPastPath,
  openDillToast,
  removeQueryParam,
  saveLoginSupplierId,
  stripSupplierPrefix,
} from '../../utils/helpers';

const loginModes = [
  'ENTER_EMAIL',
  'EMAIL_LINK_SENT',
  'ENTER_PASSWORD',
  'NEW_USER',
  'CREATE_PASSWORD',
  'DRIVERS_NOT_ALLOWED',
  'PASSWORD_RESET',
  'EMAIL_NEEDS_REVERIFICATION',
] as const;
const LoginModeSchema = z.enum(loginModes);
type LoginMode = z.infer<typeof LoginModeSchema>;

function LoginPage() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    isLoginEmailSent,
    userCheckState,
    isValidatingUser,
    firebaseUserId,
    isPasswordSet,
    isEmailVerified,
    globalPastPath,
    user,
    loadingList: authLoadingList,
  } = useAppSelector((state) => state.auth);
  const linkedSupplierId = user?.linkedSupplierIds?.[0];
  // Default landing after login. With no linked supplier there is no id to scope the
  // path to, so fall back to the id-less `/supplier` shell instead of a bare `/customers`.
  const defaultSupplierLandingPath = linkedSupplierId
    ? buildSupplierPath(linkedSupplierId, '/customers')
    : '/supplier';
  const { basicSupplier } = useAppSelector((state) => state.buyers);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [nextPageNavigationPath, setNextPageNavigationPath] = useState('');
  const [goToNextPath, setGoToNextPath] = useState({});
  const [isPasswordResetLinkSent, setIsPasswordResetLinkSent] = useState(false);
  const [isPasswordResetLinkInvalid, setIsPasswordResetLinkInvalid] = useState(false);
  const [isVerifyingResetLink, setIsVerifyingResetLink] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isAcceptTerms, setIsAcceptTerms] = useState(false);
  const [currentLoginMode, setCurrentLoginMode] = useState<LoginMode>('ENTER_EMAIL');
  const [isEmailForLinkSignInSaved, setIsEmailForLinkSignInSaved] = useState(true);
  const [customerAccountName, setCustomerAccountName] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [requestedDomainId, setRequestedDomainId] = useState<string | undefined>(undefined);
  const [hasRequestedAccess, setHasRequestedAccess] = useState(false);

  // Same check the backend runs on password update, so the rules are visible while typing instead
  // of only after a rejected submit.
  const passwordValidation = usePasswordValidation({
    password,
    email,
    personalTerms: [user?.firstName, user?.lastName, basicSupplier?.name],
    isEnabled: currentLoginMode === 'CREATE_PASSWORD' || currentLoginMode === 'PASSWORD_RESET',
  });

  const isAnyDataLoading = () => {
    const check = authLoadingList.filter((item) => item === 'checkUserExists');
    if (check.length > 0) {
      return true;
    } else {
      return false;
    }
  };

  // A dead reset code can never be recovered from this screen, so drop the code and send the user
  // back to enter their email, where they can request a fresh link.
  const handleInvalidPasswordResetLink = (errorCode?: unknown) => {
    const message = getPasswordResetErrorMessage(errorCode);
    setIsPasswordResetLinkInvalid(true);
    setIsPasswordResetLinkSent(false);
    setOobCode('');
    setPassword('');
    setErrorMessage(message);
    setCurrentLoginMode('ENTER_EMAIL');
    openDillToast({ type: 'ERROR', message });
  };

  useEffect(() => {
    if (myFirebaseAuth.currentUser && isPasswordSet && nextPageNavigationPath) {
      setGoToNextPath({});
    }

    return () => {};
  }, [nextPageNavigationPath, isPasswordSet, myFirebaseAuth.currentUser]);

  useEffect(() => {
    if (myFirebaseAuth.currentUser && isPasswordSet && nextPageNavigationPath) {
      navigate(nextPageNavigationPath);
    }

    return () => {};
  }, [goToNextPath]);

  const handleNavigationAfterUserLogin = () => {
    if (myFirebaseAuth.currentUser && isPasswordSet) {
      const pastPath = getSavedPastPath();
      const newSignIn = localStorage ? localStorage.getItem('newSignIn') : null;
      // The user is logged in at this point, so an unfinished decline saved on a previous
      // visit no longer applies and must not send them back into the declined refer flow.
      clearDeclinedReferPathForLoggedInUser();
      const referPath = localStorage ? localStorage.getItem('referPath') : null;
      if (newSignIn) {
        if (referPath) {
          const params = new URLSearchParams(referPath.split('?')[1]);
          const email1 = params.get('email');
          if (email1 && email1.toLowerCase() === myFirebaseAuth.currentUser.email) {
            setNextPageNavigationPath(referPath);
          } else {
            setNextPageNavigationPath(defaultSupplierLandingPath);
          }
          if (localStorage) {
            localStorage.removeItem('referPath');
          }
        } else if (
          pastPath &&
          stripSupplierPrefix(pastPath).startsWith('/lienReleases') &&
          pastPath.includes('email=')
        ) {
          const params = new URLSearchParams(pastPath.split('?')[1]);
          const email1 = params.get('email');
          const buyerId = params.get('buyerId');
          const supplierDomainId = params.get('supplierDomainId');
          if (
            email1 &&
            email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email &&
            buyerId
          ) {
            (async () => {
              const res = await dispatch(
                checkUserIsSetupWithCompany({ email: email1, companyId: buyerId })
              );
              if (
                res.type === 'auth/checkUserIsSetupWithCompany/fulfilled' &&
                !res.payload.isSetUp
              ) {
                setNextPageNavigationPath(
                  `/refer?email=${email1}&buyerId=${buyerId}&type=SUB_SUPPLIER&isAcceptSetup=true`
                );
              } else {
                setNextPageNavigationPath(
                  removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
                );
              }
            })();
          } else if (
            email1 &&
            email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email &&
            supplierDomainId
          ) {
            (async () => {
              const res = await dispatch(
                checkUserIsSetupWithCompany({ email: email1, supplierDomainId })
              );
              if (
                res.type === 'auth/checkUserIsSetupWithCompany/fulfilled' &&
                !res.payload.isSetUp
              ) {
                setNextPageNavigationPath(
                  `/refer?email=${email1}&supplierDomainId=${supplierDomainId}&type=BUYER&isAcceptSetup=true`
                );
              } else {
                setNextPageNavigationPath(
                  removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
                );
              }
            })();
          } else {
            setNextPageNavigationPath(
              removeQueryParam({
                path: buildSupplierPath(linkedSupplierId, '/lienReleases'),
                paramNameToRemove: 'email',
              })
            );
          }
        } else if (
          pastPath?.startsWith('/companies') &&
          pastPath?.includes('/invoices') &&
          pastPath.includes('email=')
        ) {
          const params = new URLSearchParams(pastPath.split('?')[1]);
          const email1 = params.get('email');
          let supplierDomainId = pastPath.split('?')[0].split('/').slice(-1)[0];
          supplierDomainId = supplierDomainId === 'invoices' ? '' : supplierDomainId;
          if (
            email1 &&
            email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email &&
            supplierDomainId
          ) {
            (async () => {
              const res = await dispatch(
                checkUserIsSetupWithCompany({ email: email1, supplierDomainId })
              );
              if (
                res.type === 'auth/checkUserIsSetupWithCompany/fulfilled' &&
                !res.payload.isSetUp
              ) {
                setNextPageNavigationPath(
                  `/refer?email=${email1}&supplierDomainId=${supplierDomainId}&type=BUYER&isAcceptSetup=true`
                );
              } else {
                setNextPageNavigationPath(
                  removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
                );
              }
            })();
          } else if (email1 && email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email) {
            setNextPageNavigationPath(
              removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
            );
          } else {
            setNextPageNavigationPath(
              removeQueryParam({ path: '/companies', paramNameToRemove: 'email' })
            );
          }
        } else if (pastPath) {
          setNextPageNavigationPath(pastPath);
        } else {
          setNextPageNavigationPath(defaultSupplierLandingPath);
        }
      } else if (referPath) {
        const params = new URLSearchParams(referPath.split('?')[1]);
        const email1 = params.get('email');
        if (email1 && email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email) {
          setNextPageNavigationPath(referPath);
        } else if (pastPath) {
          setNextPageNavigationPath(pastPath);
        } else {
          setNextPageNavigationPath(defaultSupplierLandingPath);
        }
        if (localStorage) {
          localStorage.removeItem('referPath');
        }
      } else if (globalPastPath) {
        setNextPageNavigationPath(globalPastPath);
      } else if (pastPath) {
        if (
          pastPath &&
          stripSupplierPrefix(pastPath).startsWith('/lienReleases') &&
          pastPath.includes('email=')
        ) {
          const params = new URLSearchParams(pastPath.split('?')[1]);
          const email1 = params.get('email');
          const buyerId = params.get('buyerId');
          const supplierDomainId = params.get('supplierDomainId');
          if (
            email1 &&
            email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email &&
            buyerId
          ) {
            (async () => {
              const res = await dispatch(
                checkUserIsSetupWithCompany({ email: email1, companyId: buyerId })
              );
              if (
                res.type === 'auth/checkUserIsSetupWithCompany/fulfilled' &&
                !res.payload.isSetUp
              ) {
                setNextPageNavigationPath(
                  `/refer?email=${email1}&buyerId=${buyerId}&type=SUB_SUPPLIER&isAcceptSetup=true`
                );
              } else {
                setNextPageNavigationPath(
                  removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
                );
              }
            })();
          } else if (
            email1 &&
            email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email &&
            supplierDomainId
          ) {
            (async () => {
              const res = await dispatch(
                checkUserIsSetupWithCompany({ email: email1, supplierDomainId })
              );
              if (
                res.type === 'auth/checkUserIsSetupWithCompany/fulfilled' &&
                !res.payload.isSetUp
              ) {
                setNextPageNavigationPath(
                  `/refer?email=${email1}&supplierDomainId=${supplierDomainId}&type=BUYER&isAcceptSetup=true`
                );
              } else {
                setNextPageNavigationPath(
                  removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
                );
              }
            })();
          } else {
            setNextPageNavigationPath(
              removeQueryParam({
                path: buildSupplierPath(linkedSupplierId, '/lienReleases'),
                paramNameToRemove: 'email',
              })
            );
          }
        } else if (
          pastPath?.startsWith('/companies') &&
          pastPath?.includes('/invoices') &&
          pastPath.includes('email=')
        ) {
          const params = new URLSearchParams(pastPath.split('?')[1]);
          const email1 = params.get('email');
          let supplierDomainId = pastPath.split('?')[0].split('/').slice(-1)[0];
          supplierDomainId = supplierDomainId === 'invoices' ? '' : supplierDomainId;
          if (
            email1 &&
            email1.toLocaleLowerCase() === myFirebaseAuth.currentUser.email &&
            supplierDomainId
          ) {
            (async () => {
              const res = await dispatch(
                checkUserIsSetupWithCompany({ email: email1, supplierDomainId })
              );
              if (
                res.type === 'auth/checkUserIsSetupWithCompany/fulfilled' &&
                !res.payload.isSetUp
              ) {
                setNextPageNavigationPath(
                  `/refer?email=${email1}&supplierDomainId=${supplierDomainId}&type=BUYER&isAcceptSetup=true`
                );
              } else {
                setNextPageNavigationPath(
                  removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
                );
              }
            })();
          } else if (email1 === myFirebaseAuth.currentUser.email) {
            setNextPageNavigationPath(
              removeQueryParam({ path: pastPath, paramNameToRemove: 'email' })
            );
          } else {
            setNextPageNavigationPath(
              removeQueryParam({ path: '/companies', paramNameToRemove: 'email' })
            );
          }
        } else {
          setNextPageNavigationPath(pastPath);
        }
      } else if (pastPath) {
        setNextPageNavigationPath(pastPath);
      } else {
        setNextPageNavigationPath(defaultSupplierLandingPath);
      }
    }
  };

  const checkIfTemporaryUser = async (emailSet: string) => {
    const response = await dispatch(getUserDetails());
    if (response.type === 'auth/getUserDetails/fulfilled') {
      const payload: any = response?.payload;
      if (payload?.data?.roles?.includes('TEMPORARY_USER')) {
        return;
      }
    }

    await dispatch(checkUserExists({ email: emailSet }));
  };

  useEffect(() => {
    if (!isAnyDataLoading()) {
      if (userCheckState === 'DRIVERS_NOT_ALLOWED') {
        setCurrentLoginMode('DRIVERS_NOT_ALLOWED');
      } else if (userCheckState === 'USER_EXISTS' && !myFirebaseAuth.currentUser) {
        if (isLoginEmailSent) {
          setCurrentLoginMode('EMAIL_LINK_SENT');
        } else if (isEmailVerified && isPasswordSet && email) {
          setCurrentLoginMode('ENTER_PASSWORD');
        } else if (isEmailVerified && !isPasswordSet && email) {
          setCurrentLoginMode('EMAIL_NEEDS_REVERIFICATION');
        } else if (isEmailVerified && isPasswordSet && !email) {
          setCurrentLoginMode('ENTER_EMAIL');
        } else if (!isEmailVerified && email && currentLoginMode !== 'EMAIL_LINK_SENT') {
          dispatch(firebaseSendLoginEmail({ email }));
          setIsEmailForLinkSignInSaved(true);
          setCurrentLoginMode('EMAIL_LINK_SENT');
        } else if (!email) {
          setCurrentLoginMode('ENTER_EMAIL');
        } else {
          // setCurrentLoginMode('CREATE_PASSWORD');
        }
      } else if (userCheckState === 'NEW_USER' && !myFirebaseAuth.currentUser) {
        setCurrentLoginMode('NEW_USER');
      } else if (myFirebaseAuth.currentUser && !isPasswordSet) {
        const params = new URLSearchParams(location.search);
        const continueUrlValue = params.get('continueUrl');

        let continueUrl: URL | null = null;
        let isValidPath = false;

        if (continueUrlValue) {
          try {
            continueUrl = new URL(continueUrlValue);
            continueUrl.searchParams.set('redirected', 'creditApp');
            const validPaths = ['requestCreditApplication', 'reviewCreditApplicationFeedback'];

            isValidPath = validPaths.some((path) => continueUrl?.pathname.includes(path));
          } catch {
            continueUrl = null;
          }
        }
        if (isValidPath && continueUrl) {
          window.location.href = continueUrl.toString();
        } else if (userCheckState === 'USER_EXISTS') {
          if (isLoginEmailSent) {
            setCurrentLoginMode('EMAIL_LINK_SENT');
          } else if (!isPasswordSet && isEmailVerified && myFirebaseAuth.currentUser.email) {
            setCurrentLoginMode('CREATE_PASSWORD');
            setEmail(myFirebaseAuth.currentUser.email);
          } else if (!isEmailVerified && email && currentLoginMode !== 'EMAIL_LINK_SENT') {
            dispatch(logoutUser());
          }
        } else {
          if (myFirebaseAuth.currentUser.email) {
            setEmail(myFirebaseAuth.currentUser.email);
            checkIfTemporaryUser(myFirebaseAuth.currentUser.email);
          }
        }
      } else if (myFirebaseAuth.currentUser && isPasswordSet) {
        if (!isEmailVerified && email && currentLoginMode !== 'EMAIL_LINK_SENT') {
          dispatch(logoutUser());
        } else {
          handleNavigationAfterUserLogin();
        }
      }
    }

    return () => {};
  }, [
    isPasswordSet,
    userCheckState,
    isLoginEmailSent,
    myFirebaseAuth.currentUser,
    isEmailVerified,
    location.pathname,
  ]);

  useEffect(() => {
    if (!myFirebaseAuth.currentUser) {
      const params = new URLSearchParams(location.search);
      const urlModeValue = params.get('mode');
      const apiKey = params.get('apiKey');
      const oobCodeValue = params.get('oobCode');
      const continueUrlValue = params.get('continueUrl');

      if (continueUrlValue) {
        try {
          const continueUrl = new URL(continueUrlValue);

          const validPaths = ['requestCreditApplication', 'reviewCreditApplicationFeedback'];

          const isValidPath = validPaths.some((path) => continueUrl.pathname.includes(path));

          if (isValidPath) {
            const loginParams = { apiKey, oobCode: oobCodeValue, mode: urlModeValue };

            Object.entries(loginParams).forEach(([key, value]) => {
              if (value) continueUrl.searchParams.set(key, value);
            });

            window.location.href = continueUrl.toString();
            return;
          }
        } catch (err) {
          console.error('Invalid continueUrl:', err);
        }
      }
      // complete user login after clicking link in email
      if (oobCodeValue) {
        setOobCode(oobCodeValue);
      }
      if (urlModeValue === '' && apiKey) {
        dispatch(firebaseConfirmLoginUser({ url: window.location.href }));
      }
      if (urlModeValue === 'signIn' && apiKey) {
        if (localStorage) {
          localStorage.setItem('newSignIn', 'newSignIn');
        }
        const emailForSignIn = localStorage ? localStorage.getItem('emailForSignIn') : null;
        if (emailForSignIn) {
          dispatch(firebaseConfirmLoginUser({ url: window.location.href }));
          setIsEmailForLinkSignInSaved(true);
          setEmail(emailForSignIn);
        } else {
          setIsEmailForLinkSignInSaved(false);
          openDillToast({
            type: 'ERROR',
            message:
              'Failed to verify your email. You might have opened the verification link for a different browser. You can resend a verification email for this browser by entering your email below or open the verification link with the previous browser',
          });
        }
      }

      if (urlModeValue === 'resetPassword' && apiKey && oobCodeValue) {
        setPassword('');
        setOobCode(oobCodeValue);
        const emailForPasswordReset = localStorage
          ? localStorage.getItem('emailForPasswordReset')
          : null;
        if (emailForPasswordReset) {
          setEmail(emailForPasswordReset);
        }
        setCurrentLoginMode('PASSWORD_RESET');
        // Check the link before asking for a password, so a stale link is reported straight away
        // instead of after the user has typed one.
        (async () => {
          setIsVerifyingResetLink(true);
          try {
            const results = await dispatch(
              firebaseVerifyPasswordResetCode({ oobCode: oobCodeValue })
            );
            if (firebaseVerifyPasswordResetCode.rejected.match(results)) {
              // A dropped connection says nothing about the link, so hold on to the code and let
              // them retry rather than declaring a good link dead.
              if (isRetryablePasswordResetError(results.payload)) {
                setErrorMessage(getPasswordResetErrorMessage(results.payload));
              } else {
                handleInvalidPasswordResetLink(results.payload);
              }
            } else {
              setIsPasswordResetLinkInvalid(false);
              setErrorMessage('');
              // The link is authoritative for the account email, unlike localStorage which is
              // empty when the link is opened on another browser or device.
              if (results.payload?.email) {
                setEmail(results.payload.email);
              }
            }
          } finally {
            setIsVerifyingResetLink(false);
          }
        })();
      }

      if (location.pathname.startsWith('/portal/') || location.pathname.startsWith('/login/')) {
        // gets supplier details when use portal path
        const pathSplit = location.pathname.split('/');
        if (pathSplit.length > 2) {
          dispatch(getBasicSupplier({ supplierDomainId: pathSplit[2] }));
        }
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    const isSSO = sessionStorage.getItem('isSSO');
    if (email && myFirebaseAuth?.currentUser?.email && isSSO) {
      dispatch(checkUserExists({ email: myFirebaseAuth.currentUser.email, thenLogIn: false }));
      sessionStorage.removeItem('isSSO');
    }
  }, [email]);

  useEffect(() => {
    const emailFromLink = sessionStorage.getItem('emailFromLink');
    if (emailFromLink && !myFirebaseAuth.currentUser) {
      setEmail(emailFromLink);
    }
    if (
      emailFromLink &&
      (!myFirebaseAuth.currentUser ||
        (myFirebaseAuth.currentUser &&
          myFirebaseAuth.currentUser.email !== emailFromLink.toLocaleLowerCase()))
    ) {
      (async () => {
        await dispatch(logoutUser());
        setEmail(emailFromLink);
        sessionStorage.removeItem('emailFromLink');
        dispatch(checkUserExists({ email: emailFromLink, thenLogIn: false }));
      })();
    }
    if (
      emailFromLink &&
      myFirebaseAuth.currentUser &&
      myFirebaseAuth.currentUser.email !== emailFromLink.toLocaleLowerCase()
    ) {
      setEmail(emailFromLink);
      if (localStorage) {
        localStorage.removeItem('newSignIn');
      }
      dispatch(logoutUser());
      setNextPageNavigationPath('/login');
    }
  }, [myFirebaseAuth.currentUser, userCheckState]);

  useEffect(() => {
    // Get basic supplier details for the past supplier whose invoices where seen by the user
    // setEmail('');
    setPassword('');
    setErrorMessage('');
    setIsAcceptTerms(false);
    const pastPath = getSavedPastPath();
    if (pastPath && pastPath.startsWith('/companies/')) {
      const pathSplit = pastPath.split('/');
      if (pathSplit.length > 3) {
        dispatch(getBasicSupplier({ supplierDomainId: pathSplit[4] }));
      }
    }
  }, []);

  useEffect(() => {
    if (currentLoginMode === 'NEW_USER') {
      setHasRequestedAccess(false);
      setCustomerAccountName('');
      (async () => {
        let domainId: string | undefined = undefined;
        if (location.pathname.startsWith('/portal/') || location.pathname.startsWith('/login/')) {
          const pathSplit = location.pathname.split('/');
          if (pathSplit.length > 2) {
            const ress = await dispatch(getBasicSupplier({ supplierDomainId: pathSplit[2] }));
            if (ress.type === 'buyers/getBasicSupplier/fulfilled') {
              const payload: any = ress?.payload;
              domainId = payload?.data?.domainId;
            }
          }
        }
        setRequestedDomainId(basicSupplier?.domainId || domainId || undefined);
      })();
    }
  }, [currentLoginMode]);

  useEffect(() => {
    // Navigate to the login page of the supplier whose basic details have been fetched
    if (basicSupplier) {
      // Remember which supplier's portal this login came through so the post-login redirects
      // can default to a main buyer of that supplier instead of the first one alphabetically.
      saveLoginSupplierId(basicSupplier.id);
      setNextPageNavigationPath(`/login/${basicSupplier.domainId}`);
    }
  }, [basicSupplier]);

  useEffect(() => {
    if (location.pathname.startsWith('/login')) {
      const params = new URLSearchParams(location.search);
      const emailFromLink = params.get('email');
      if (emailFromLink) {
        setEmail(emailFromLink);
        sessionStorage.setItem('emailFromLink', emailFromLink);
        dispatch(checkUserExists({ email: emailFromLink, thenLogIn: false }));
        navigate('/login');
        // navigate(0);
      }
    }

    return () => {};
  }, [location.pathname, myFirebaseAuth.currentUser]);

  const handleLoginNextClick = async () => {
    // checks if user exists, then login if the user is in our db
    if (email) {
      setErrorMessage('');
      if (
        myFirebaseAuth?.currentUser?.email &&
        myFirebaseAuth?.currentUser?.email.toLocaleLowerCase() !== email.toLocaleLowerCase()
      ) {
        await dispatch(logoutUser());
      }
      const results = await dispatch(checkUserExists({ email, thenLogIn: false }));
      if (results.payload && results.payload.success) {
        setPassword('');
        setIsEmailForLinkSignInSaved(true);
      } else {
        setErrorMessage('');
        setCurrentLoginMode('NEW_USER');
      }
    } else {
      setErrorMessage('Enter Email address');
    }
  };
  const handleLoginPasswordClick = async () => {
    if (email && password) {
      const results = await dispatch(firebaseLoginEmailAndPassword({ email, password }));
      if (results.type === 'auth/firebaseLoginEmailAndPassword/rejected') {
        setErrorMessage(getLoginErrorMessage(results.payload));
      }
    } else {
      if (!email) {
        setErrorMessage('Email not set, try using different email');
      }
      if (!password) {
        setErrorMessage('Enter Password');
      }
    }
  };
  const handleResetPasswordClick = async () => {
    // confirmPasswordReset consumes the code, so a second click would come back as an invalid
    // code and report a dead link for a reset that actually succeeded.
    if (isResettingPassword) {
      return;
    }
    if (password && !passwordValidation.isValid) {
      setErrorMessage(passwordValidation.errorMessage);
    } else if (password && oobCode) {
      setIsResettingPassword(true);
      let results;
      try {
        results = await dispatch(firebaseConfirmPasswordReset({ newPassword: password, oobCode }));
      } finally {
        setIsResettingPassword(false);
      }
      if (firebaseConfirmPasswordReset.rejected.match(results)) {
        if (isStalePasswordResetCodeError(results.payload)) {
          handleInvalidPasswordResetLink(results.payload);
        } else {
          // Stay on the reset form so the password can be corrected and resubmitted. Covers weak
          // passwords and the retryable network/rate-limit cases, where the code is still good.
          setErrorMessage(getPasswordResetErrorMessage(results.payload));
        }
      } else {
        setErrorMessage('');
        setPassword('');
        setIsPasswordResetLinkInvalid(false);
        setNextPageNavigationPath('/login');
        if (email) {
          setCurrentLoginMode('ENTER_PASSWORD');
        } else {
          setCurrentLoginMode('ENTER_EMAIL');
        }
      }
    } else {
      if (!password) {
        setErrorMessage('Enter Password');
      }
      if (!oobCode) {
        setErrorMessage('Error resetting password. Try again');
        setCurrentLoginMode('ENTER_PASSWORD');
      }
    }
  };
  const handlePasswordSetClick = async () => {
    if (password && !passwordValidation.isValid) {
      setErrorMessage(passwordValidation.errorMessage);
    } else if (password) {
      const results = await dispatch(updateLoggedInUserPassword({ email, password }));
      if (results.type === 'auth/updateLoggedInUserPassword/rejected') {
        if (results.payload.message) {
          setErrorMessage(results.payload.message);
        } else {
          setErrorMessage('Invalid Password');
        }
      } else if (results.type === 'auth/updateLoggedInUserPassword/fulfilled') {
        setPassword('');
      }
    } else {
      if (!password) {
        setErrorMessage('Enter Password');
      }
    }
  };
  const handleResendEmail = async () => {
    if (email) {
      // When the check was already done, we just login
      if (userCheckState === 'USER_EXISTS') {
        await dispatch(firebaseSendLoginEmail({ email, overrideCooldown: true }));
        setIsEmailForLinkSignInSaved(true);
        openDillToast({
          type: 'SUCCESS',
          message: 'A verification email has been resent.',
        });
      } else if (userCheckState === 'DRIVERS_NOT_ALLOWED') {
      } else {
        await dispatch(checkUserExists({ email, thenLogIn: true }));
        openDillToast({
          type: 'SUCCESS',
          message: 'A verification email has been resent.',
        });
      }
    } else {
      await dispatch(updateSendLoginEmailState(false));
    }
  };

  const handleUseDifferentEmail = async () => {
    await dispatch(logoutUser());
    if (localStorage) {
      localStorage?.removeItem('pastPathList');
      sessionStorage?.removeItem('emailFromLink');
      localStorage?.removeItem('newSignIn');
      localStorage?.removeItem('emailForSignIn');
    }
    // Starting over: drop the previous portal's supplier so it can't bias the next login. It is
    // re-saved below when the current path still points at a supplier portal.
    clearLoginSupplierId();

    setEmail('');
    dispatch(updateSendLoginEmailState(false));
    dispatch(resetUserCheck());
    // navigate('/login');
    setNextPageNavigationPath('/login');
    setCurrentLoginMode('ENTER_EMAIL');
    setErrorMessage('');
    setIsPasswordResetLinkInvalid(false);
    setIsPasswordResetLinkSent(false);
    if (location.pathname.startsWith('/portal/') || location.pathname.startsWith('/login/')) {
      // gets supplier details when use portal path
      const pathSplit = location.pathname.split('/');
      if (pathSplit.length > 2) {
        dispatch(getBasicSupplier({ supplierDomainId: pathSplit[2] }));
      }
    }
  };

  const handleRequestAccess = () => {
    // Runs in every environment: the request has to be recorded for the follow up lists to work,
    // and sendEmailService already gates non-production sends to the testing addresses.
    dispatch(
      newUserNotifyDill({
        email,
        domainId: requestedDomainId,
        customerAccountName: customerAccountName || undefined,
        supplierName: !requestedDomainId ? supplierName || undefined : undefined,
      })
    );
    setHasRequestedAccess(true);
  };

  const handleSendResetPasswordEmail = async () => {
    if (email) {
      const results = await dispatch(firebaseSendPasswordResetEmail({ email }));
      if (firebaseSendPasswordResetEmail.rejected.match(results)) {
        setIsPasswordResetLinkSent(false);
        setErrorMessage("Couldn't send the password reset email. Please try again.");
      } else {
        setIsPasswordResetLinkSent(true);
        setIsPasswordResetLinkInvalid(false);
        setErrorMessage('');
      }
    } else {
      setErrorMessage('Email not set, try using different email');
    }
  };

  return (
    <div className="flex w-full h-full justify-center items-center px-4 py-8">
      <div className="flex flex-col w-full max-w-sm md:max-w-md lg:max-w-lg items-center justify-center">
        {basicSupplier && basicSupplier.logo && basicSupplier.logo?.url && (
          <div className="flex flex-col items-center mb-6">
            <img className="max-h-16 md:max-h-20 w-auto" src={basicSupplier.logo.url} />
            <div className="flex items-center mt-1">
              <p className="text-sm">Powered By</p>
              <a href="https://usedill.com/" target="_blank">
                <img className="h-6 md:h-8 ml-2" src={dillLogoWithText} />
              </a>
            </div>
          </div>
        )}
        {(!basicSupplier || (basicSupplier && !basicSupplier.logo?.url)) && (
          <div className="flex flex-col items-center mb-8 md:mb-14 w-full">
            <img className="w-3/4 max-w-xs md:max-w-sm" src={dillLogoWithText} />
          </div>
        )}

        <div className="flex flex-col GREY_100-BG px-6 md:px-14 py-4 md:py-5 rounded-xl w-full">
          {currentLoginMode === 'ENTER_EMAIL' && (
            <div className="flex flex-col">
              {!isEmailForLinkSignInSaved && (
                <>
                  <p className="text-center text-xs">
                    You might have opened the email verification link for a different browser. A new
                    link for this browser will be sent when you enter your email below
                  </p>
                </>
              )}
              <p className="text-center font-bold text-sm mt-2">Enter your email address</p>

              <div className="mb-6 mt-3 flex flex-col sm:flex-row w-full gap-3 sm:gap-2">
                <div className="w-full">
                  <AppInputField
                    value={email}
                    inputType="email"
                    placeholder="Your email address"
                    onTextChange={(text) => {
                      setEmail(text);
                    }}
                  />
                </div>

                <div className="sm:flex-shrink-0 flex justify-center sm:justify-start">
                  <AppButton text={'Next'} onClick={handleLoginNextClick} />
                </div>
              </div>
              {isPasswordResetLinkInvalid && (
                <p className="text-center mb-3">
                  <span
                    className="PRIMARY_500-CLR font-bold cursor-pointer text-xs"
                    onClick={handleSendResetPasswordEmail}>
                    Send me a new password reset link
                  </span>
                </p>
              )}
              {basicSupplier?.note && (
                <div className="mb-6 mt-3 flex w-full text-center text-xs">
                  {basicSupplier?.note}
                </div>
              )}
            </div>
          )}
          {currentLoginMode === 'EMAIL_LINK_SENT' && (
            <div className="flex flex-col">
              <p className="text-center font-bold text-sm">
                Check your email for a verification link to sign in to your portal. *Make sure to
                open the link from this browser, and to immediately set your password after
                verifying your email*
              </p>
              <p className="text-center text-sm mt-6">
                Didn't receive the link?{' '}
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer"
                  onClick={handleResendEmail}>
                  Resend here
                </span>
              </p>
              <p className="text-center text-sm mt-6">
                If you're still not receiving the link, check with your IT department to whitelist
                the domain <span className="PRIMARY_500-CLR ">usedill.com</span>
              </p>
              <p className="text-center mt-4">
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                  onClick={handleUseDifferentEmail}>
                  Use a different email
                </span>
              </p>
              <p className="text-2xs text-center">
                Current email: <span className="PRIMARY_500-CLR ">{email}</span>
              </p>
            </div>
          )}
          {currentLoginMode === 'NEW_USER' && (
            <div className="flex flex-col gap-3">
              {!hasRequestedAccess ? (
                <>
                  <p className="text-center font-bold text-sm">
                    We see that you are a new user. Enter your company name below to request access.
                    Or reach out to{' '}
                    <span className="PRIMARY_500-CLR font-bold whitespace-nowrap">
                      (628) 304-0931
                    </span>{' '}
                    or{' '}
                    <span className="PRIMARY_500-CLR font-bold whitespace-nowrap">
                      support@usedill.com
                    </span>{' '}
                    directly.
                  </p>
                  <AppInputField
                    label="Company / Account Name"
                    placeholder="Your company name"
                    value={customerAccountName}
                    onTextChange={(text) => setCustomerAccountName(text)}
                  />
                  {!requestedDomainId && (
                    <AppInputField
                      label="Supplier Name"
                      placeholder="Which supplier do you need access to?"
                      value={supplierName}
                      onTextChange={(text) => setSupplierName(text)}
                    />
                  )}
                  <div className="flex justify-center">
                    <AppButton text="Request Access" onClick={handleRequestAccess} />
                  </div>
                </>
              ) : (
                <p className="text-center font-bold text-sm">
                  {requestedDomainId && basicSupplier?.email ? (
                    <>
                      Access request sent to {basicSupplier.email}! Someone from{' '}
                      {basicSupplier?.name || supplierName} will be in touch shortly.
                    </>
                  ) : (
                    'Access request sent! Someone from Dill will be in touch shortly.'
                  )}
                </p>
              )}
              <div className="flex flex-col">
                <p className="text-center mt-2">
                  <span
                    className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                    onClick={handleUseDifferentEmail}>
                    Use a different email
                  </span>
                </p>
                <p className="text-2xs text-center">
                  Current email: <span className="PRIMARY_700-CLR ">{email}</span>
                </p>
              </div>
            </div>
          )}
          {currentLoginMode === 'DRIVERS_NOT_ALLOWED' && (
            <div className="flex flex-col">
              <div className="w-full mr-2 text-center">
                You currently don't have access. Reach out to your company admin to request access
                to the Dill web portal
              </div>
              <div className="flex flex-col">
                <p className="text-center mt-4">
                  <span
                    className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                    onClick={handleUseDifferentEmail}>
                    Use a different email
                  </span>
                </p>
                <p className="text-2xs text-center">
                  Current email: <span className="PRIMARY_700-CLR ">{email}</span>
                </p>
              </div>
            </div>
          )}
          {currentLoginMode === 'ENTER_PASSWORD' && (
            <div className="flex flex-col">
              <p className="text-center font-bold text-sm">Enter your password</p>
              <div className="my-6 flex flex-col sm:flex-row w-full gap-3 sm:gap-2">
                <div className="w-full">
                  <AppInputField
                    value={password}
                    inputType="password"
                    placeholder="Your password"
                    onTextChange={(text) => {
                      setPassword(text);
                    }}
                  />
                </div>

                <div className="sm:flex-shrink-0 flex justify-center sm:justify-start">
                  <AppButton text="Sign In" onClick={handleLoginPasswordClick} />
                </div>
              </div>

              <div className="flex flex-col">
                <p className="text-center mt-4">
                  <span
                    className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                    onClick={handleUseDifferentEmail}>
                    Use a different email
                  </span>
                </p>
                <p className="text-2xs text-center">
                  Current email: <span className="PRIMARY_700-CLR ">{email}</span>
                </p>
              </div>
            </div>
          )}
          {currentLoginMode === 'PASSWORD_RESET' && (
            <div className="flex flex-col">
              <p className="text-center font-bold text-sm">Reset your password</p>
              {isVerifyingResetLink ? (
                // Hold the password field back until the link is known to be good, so a dead link
                // never gets a password typed into it.
                <p className="text-center text-xs my-6">Checking your reset link…</p>
              ) : (
                <>
                  <div className="mt-6 flex flex-col sm:flex-row w-full gap-3 sm:gap-2">
                    <div className="w-full">
                      <AppInputField
                        value={password}
                        inputType="password"
                        placeholder="Your password"
                        onTextChange={(text) => {
                          setPassword(text);
                        }}
                      />
                    </div>

                    <div className="sm:flex-shrink-0 flex justify-center sm:justify-start">
                      <AppButton
                        text="Update"
                        onClick={handleResetPasswordClick}
                        isDisabled={isResettingPassword || !passwordValidation.isValid}
                        isLoading={isResettingPassword}
                      />
                    </div>
                  </div>
                  <AppPasswordRequirements
                    requirements={passwordValidation.requirements}
                    className="mt-3 mb-6"
                  />
                </>
              )}
              <p className="text-center mt-4">
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                  onClick={handleUseDifferentEmail}>
                  Use a different email
                </span>
              </p>
              {email && (
                <p className="text-2xs text-center">
                  Current email: <span className="PRIMARY_700-CLR ">{email}</span>
                </p>
              )}
            </div>
          )}
          {currentLoginMode === 'CREATE_PASSWORD' && (
            <div className="flex flex-col">
              <p className="text-center font-bold text-sm">Please set a password</p>
              <div className="mt-6 flex w-full">
                <div className="w-full">
                  <AppInputField
                    value={password}
                    inputType="password"
                    placeholder="Your password"
                    onTextChange={(text) => {
                      setPassword(text);
                    }}
                  />
                </div>
              </div>
              <AppPasswordRequirements
                requirements={passwordValidation.requirements}
                className="mt-3 mb-6"
              />
              <div className="flex mb-3">
                <AppCheckBox
                  isChecked={isAcceptTerms}
                  onClick={() => {
                    setIsAcceptTerms(!isAcceptTerms);
                  }}
                  className="px-1 py-2  mx-0 border-red-500"
                  checkSize={12}
                />
                <p className="text-xs md:text-sm text-left ml-2 space-y-2 w-full font-medium">
                  By checking this box you agree to{' '}
                  <a
                    href="https://www.usedill.com/terms"
                    target="_blank"
                    className="PRIMARY_500-CLR underline">
                    Our Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.usedill.com/privacy"
                    target="_blank"
                    className="PRIMARY_500-CLR underline">
                    Privacy Policy
                  </a>
                  , as well as our partner{' '}
                  <a
                    href="https://www.dwolla.com/legal/tos/"
                    target="_blank"
                    className="PRIMARY_500-CLR underline">
                    Dwolla's Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.dwolla.com/legal/privacy/"
                    target="_blank"
                    className="PRIMARY_500-CLR underline">
                    Privacy Policy
                  </a>
                </p>
              </div>
              <div className="flex justify-center">
                <AppButton
                  text="Agree & Continue"
                  onClick={handlePasswordSetClick}
                  isDisabled={!isAcceptTerms || !passwordValidation.isValid}
                />
              </div>

              <p className="text-center mt-4">
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                  onClick={handleUseDifferentEmail}>
                  Use a different email
                </span>
              </p>
              <p className="text-2xs text-center">
                Current email: <span className="PRIMARY_700-CLR ">{email}</span>
              </p>
            </div>
          )}
          {currentLoginMode === 'EMAIL_NEEDS_REVERIFICATION' && (
            <div className="flex flex-col">
              <p className="text-center font-bold text-sm">
                Your email needs to be reverified. This could be that you did not set your password
                immediately after you last verified your email
              </p>

              <div className="flex justify-center mt-1">
                <AppButton text="Resend verification link" onClick={handleResendEmail} />
              </div>

              <p className="text-center mt-4">
                <span
                  className="PRIMARY_500-CLR font-bold cursor-pointer text-sm"
                  onClick={handleUseDifferentEmail}>
                  Use a different email
                </span>
              </p>
              <p className="text-2xs text-center">
                Current email: <span className="PRIMARY_700-CLR ">{email}</span>
              </p>
            </div>
          )}
        </div>
        {currentLoginMode === 'ENTER_PASSWORD' && (
          <p className="text-center mt-4">
            <span
              className="PRIMARY_500-CLR font-bold cursor-pointer text-xs"
              onClick={handleSendResetPasswordEmail}>
              Forgot password? Send password reset link
            </span>
          </p>
        )}
        {errorMessage && <p className="ERROR_500-CLR text-sm mt-3">{errorMessage}</p>}
        {isPasswordResetLinkSent && (
          <div className="flex items-center py-2 px-3 SUCCESS_100-BG  SUCCESS_900-CLR rounded-md mt-2">
            <Checks size={20} />
            <p className="text-sm  ml-2">Password reset link sent to your email!</p>
          </div>
        )}
        {basicSupplier && (
          <div className="w-full flex items-center py-2 px-3 text-xs PRIMARY_500-CLR PRIMARY_100-BG rounded-md mt-2 gap-2">
            <div>
              <ShieldCheck size={24} weight="fill" />
            </div>
            <div>
              Easy, secure payments for your convenience. Use this portal to retrieve invoices and
              statements and pay online by bank ACH or card.
            </div>
          </div>
        )}
        {basicSupplier && (basicSupplier?.email || basicSupplier?.phoneNumber) && (
          <div className="mt-2 w-full rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-2">
              Vendor Contact
            </p>
            <div className="flex flex-col gap-1.5">
              {basicSupplier?.email && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-gray-400 w-10">Email</span>
                  <a
                    href={`mailto:${basicSupplier.email}`}
                    className="text-xs font-semibold PRIMARY_500-CLR hover:underline truncate">
                    {basicSupplier.email}
                  </a>
                </div>
              )}
              {basicSupplier?.phoneNumber && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-gray-400 w-10">Phone</span>
                  <a
                    href={`tel:${basicSupplier.phoneNumber}`}
                    className="text-xs font-semibold TEXT_PRIMARY-CLR hover:underline">
                    {basicSupplier.phoneNumber}
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LoginPage;
